import app from 'flarum/forum/app';
// Phase D2: 上传改走 ziven-core 共享方法, 随 storage_provider 自动切 COS / 七牛 / 本地。
// 旧的「拿 scheduler 自己的 COS 签名 → 拼 FormData → XHR 直传」已删除 (Phase D1 uploadFile.js
// 的文件头有完整对比表)。下方 uploadMedia 现在只是一层薄封装。
import { uploadFile } from 'ext:ziiven/ziven-core/common/helpers/uploadFile';
export function openMediaLightbox(items, index) {
  if (index === void 0) {
    index = 0;
  }
  const list = (items || []).filter(m => m && m.url);
  if (list.length === 0) return;
  const slides = list.map(m => ({
    src: m.url,
    // ⚠️ 见上方「为什么是 html5video」
    type: m.type === 'video' ? 'html5video' : 'image'
  }));
  const doOpen = () => {
    if (window.Fancybox && window.Fancybox.show) {
      window.Fancybox.show(slides, {
        startIndex: index,
        dragToClose: false
      });
      liftAboveModals();
      return true;
    }
    return false;
  };
  if (doOpen()) return;

  // 触发 ziven-core 加载 fancybox, 然后轮询等
  if (app && app.fancyboxOpen) app.fancyboxOpen(list[0].url);
  let attempts = 0;
  const timer = setInterval(() => {
    attempts++;
    if (doOpen() || attempts > 50) {
      clearInterval(timer);
      if (attempts > 50) console.error('[zs] Fancybox load timeout');
    }
  }, 100);
}

/**
 * 判断一个媒体文件是「视频」还是「图片」。
 *
 * 🔴 Phase D2 顺手修的存量 bug (不是本轮引入的, 但卡住了本轮验收):
 *   `LessonReviews.js` 和 `LessonReviewModal.js` 一直在 import 这个函数,
 *   但**从来没有人在 LessonReviewMedia.js 里定义过它** —— 它在最初的
 *   v0.18.0 commit `66b42d9` 里就被引用了, 却漏了实现。
 *   后果: webpack 只报一条 WARNING (export not found), 编译**照常通过**;
 *   运行时 `flarum.reg.get()` 拿到的是 undefined ⇒ `LessonReviewModal.onPickMedia`
 *   在**上传成功之后**执行 `mediaTypeFor(file)` 抛 TypeError, 被自己的 catch 吞成
 *   「上传失败: mediaTypeFor is not a function」。于是文件已经传上去了, 用户却看到
 *   失败提示, 而且这条课评的媒体一条也没加上 (存储里还多出一个孤儿文件)。
 *   也就是说课评媒体上传这条路在 D2 之前就是**坏的**。
 *
 *   这里按实际用法补齐: 返回 'video' / 'image', 供 modal 的 <video>/<img> 分支
 *   和 openMediaLightbox 的 'html5video'/'image' 滑块类型使用。
 *
 * ⚠️ `file.type` 浏览器有时是空串 (某些 Windows 上传的 mp4 就是),
 *    所以再按扩展名兜底判一次, 否则真实视频会被当成图片渲染成 <img> (裂图)。
 */
export function mediaTypeFor(file) {
  const mime = String(file && file.type || '').toLowerCase();
  if (mime.indexOf('video/') === 0) return 'video';
  const name = String(file && file.name || '').toLowerCase();
  const ext = name.split('.').pop() || '';
  if (['mp4', 'webm', 'mov', 'm4v', 'ogv', 'avi', 'mkv', '3gp'].indexOf(ext) !== -1) {
    return 'video';
  }
  return 'image';
}

/**
 * 上传一个媒体文件, 返回可访问 URL。
 *
 * Phase D2 (v0.18.16): 内部已换成 ziven-core 的 `uploadFile()`, 不再硬编码 COS。
 * 调用方 (LessonReviewModal.onPickMedia) 拿到的是**绝对 URL**, 三档统一, 不用改。
 *
 * ⚠️ key 前缀仍用 `scheduler_lesson_review` —— 与卡片背景的 `scheduler_card_bg` 分开,
 *    将来清理某一类资源时不会连带删掉另一类。
 * ⚠️ 只回 URL 不回 key: 课评媒体**没有删除接口**, 用不到 key (合同/形象那些要入库的
 *    调用点才需要 uploadFileResult)。
 */
export async function uploadMedia(file) {
  // reg.get() 在非 debug 模式下找不到模块时只 console.warn + 返 undefined ⇒
  // 兜底必须自己判, 否则报错会变成毫无线索的 "uploadFile is not a function"。
  if (typeof uploadFile !== 'function') {
    throw new Error('ziven-core 上传组件未就绪, 请刷新页面后重试');
  }
  return uploadFile(file, {
    prefix: 'scheduler_lesson_review'
  });
}

/**
 * 🔴 z-index —— 真机实测后的**局部**方案 (辉哥 10/18 明确不许全局改 --fancybox-zIndex)
 *
 * ## 实测数字 (Playwright getComputedStyle, 桌面 1280)
 *   fancybox 容器      z-index = **1050** (fancybox.css 的默认值, 本轮**一个字节没改**)
 *   Flarum ModalManager 每层 = calc(1050 + var(--modal-number)) ⇒ 第 1 层 1050 / 第 2 层 1051 …
 *   ⇒ **只要课评 modal 不是第一层, 灯箱就一定被压在下面**。
 *
 * ## 走过的弯路 (别再走一遍)
 *   第一版是「打开灯箱时把**课评 modal** 抬到 1055」。实测结果恰恰相反:
 *   modalZ=1055 > fancyboxZ=1050 ⇒ 灯箱被 modal 盖住 (elementFromPoint 命中 modal 而不是灯箱)。
 *   方向搞反了 —— 要压住别人的永远是**灯箱**, 不是 modal。
 *
 * ## 本方案
 *   只给**这一个灯箱实例**的容器写 inline z-index (取所有 ModalManager 层最大值 +1),
 *   关掉时随节点一起消失。⚠️ 不碰 `--fancybox-zIndex` 变量、不碰 CSS 文件,
 *   所以 bjxy 的评价图库等其它使用方完全不受影响 (辉哥 10/6 拍板的跨扩展隔离要求)。
 *   ⚠️ 数值刻意只比当前最高 modal 高 1, 不拉到 100000: 那会让灯箱反盖住
 *   `.zs-rsv-*` (99997+) 那几层, 而那几层本来就该在最上面。
 */
function liftAboveModals() {
  setTimeout(() => {
    const containers = document.querySelectorAll('.fancybox, .fancybox__container');
    if (!containers.length) return;
    let top = 0;
    document.querySelectorAll('.ModalManager').forEach(el => {
      const z = parseInt(getComputedStyle(el).zIndex, 10);
      if (!isNaN(z) && z > top) top = z;
    });
    const target = top > 0 ? top + 1 : 1051;
    containers.forEach(el => {
      el.style.zIndex = String(target);
    });
  }, 0);
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/LessonReviewMedia', { openMediaLightbox: openMediaLightbox,mediaTypeFor: mediaTypeFor, });